import type { PulseEvent } from './pulseSchema';

/** Pre-immediate-send localStorage key. Read once on boot, then never written. */
export const LEGACY_OUTBOUND_STORAGE_KEY = 'pulse_outbound_batch';
const LEGACY_OUTBOUND_LOCK_KEY = 'pulse_outbound_flush_lock';

const safeParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const newId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `pulse_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

/** Normalize legacy `{ payload }[]` / `{ events }` queue shape → `PulseEvent[]`. */
const normalizeEvents = (raw: unknown): PulseEvent[] => {
  if (!raw) return [];
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && 'events' in raw
      ? (raw as { events: unknown }).events
      : [];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      if (item && typeof item === 'object' && 'payload' in item) {
        return (item as { payload: PulseEvent }).payload;
      }
      return item as PulseEvent;
    })
    .filter((e) => e && typeof e === 'object' && e.event_id);
};

/**
 * Take leftover events from the retired outbound queue and delete the keys.
 * New events are never written here.
 */
export const takeLegacyOutboundEvents = (): PulseEvent[] => {
  try {
    const events = normalizeEvents(
      safeParse<unknown>(localStorage.getItem(LEGACY_OUTBOUND_STORAGE_KEY), null)
    );
    localStorage.removeItem(LEGACY_OUTBOUND_STORAGE_KEY);
    localStorage.removeItem(LEGACY_OUTBOUND_LOCK_KEY);
    return events;
  } catch {
    return [];
  }
};

/** UUID helper for view_id / other non-event ids. */
export const createEventId = (): string => newId();

/**
 * Spec: `concat(customer_id, unix_timestamp)` (milliseconds).
 * customer_id is `CurrentUser.id` (loyalty and guest). Falls back to `guest` only when id is missing.
 * Note: `event_time` on the envelope remains unix seconds — only `event_id` uses ms here.
 */
export const createPulseEventId = (
  userId: string | number | null | undefined,
  unixTimestampMs: number = Date.now()
): string => {
  const id =
    userId !== null && userId !== undefined && String(userId) !== ''
      ? String(userId)
      : 'guest';
  return `${id}${unixTimestampMs}`;
};
